import Vector from './math/Vector';
import Matrix from './math/Matrix';
import CoordinateConverter from './math/coordinates/CoordinateConversion';

const D = 100;
const defaultLineWidth = 4;
const converter = new CoordinateConverter();
let initialDistance = 0;
let currentScale = 1;


class VectorCanvas {
    constructor(canvas) {
        this.canvas = canvas;
        this.ctx = this.canvas.getContext("2d");
        this.setupCanvas();

        this.xBasis = new Vector([1, 0], 'GREEN', "u");
        this.yBasis = new Vector([0, 1], 'RED', "v");
        this.iHat = new Vector([1, 0], 'GREEN');
        this.jHat = new Vector([0, 1], 'RED');

        this.ctx = this.canvas.getContext('2d');
        this.basisVectors = [this.xBasis, this.yBasis];
        this.eigenvectors = [new Vector([1, 0], '#226622'), new Vector([0, 1], '#992222')];

        this.nonBasisVectors = [];
        this.translation = { x: 0, y: 0 };
        this.scale = 1;

        //used for panning
        this.isPanning = false;
        this.startTranslation = { x: 0, y: 0 };
        this.startScreenCoordinates = { x: 0, y: 0 };
        this.ORIGIN = new Vector([0, 0], '#000000');

        this.originalVectors = [];

        this.setupCanvas();
        this.width = this.canvas.width;
        this.height = this.canvas.height;

        this.bindEvents();
    }

    setBasis(a, b, c, d) {
        this.xBasis.x = a;
        this.yBasis.x = b;
        this.xBasis.y = c;
        this.yBasis.y = d;
    }

    setupCanvas() {
        this.resizeCanvas();
        window.addEventListener('resize', () => this.resizeCanvas());
    }

    resizeCanvas() {
        this.canvas.width = window.innerWidth;
        this.canvas.height = window.innerHeight;
    }

    addVector(vector) {
        this.nonBasisVectors.push(vector);
    }

    bindEvents() {
        this.canvas.addEventListener('mousedown', event => this.onMouseDown(event));
        this.canvas.addEventListener('mousemove', event => this.onMouseMove(event));
        this.canvas.addEventListener('mouseup', () => this.onMouseUp());
        this.canvas.addEventListener('contextmenu', event => event.preventDefault());
        this.canvas.addEventListener('wheel', event => this.onZoom(event));

        // Mobile Touch Events
        this.canvas.addEventListener('touchstart', event => this.onTouch(event));
        this.canvas.addEventListener('touchmove', event => this.onTouchMove(event));
        this.canvas.addEventListener('touchend', () => this.onTouchEnd());
        // this.canvas.addEventListener('touchcancel', () => this.onTouchCancel());
    }

    onMouseDown(event) {
        if (event.button === 2) {
            this.isPanning = true;
            this.startTranslation = {
                x: this.translation.x,
                y: this.translation.y,
            };
            this.startScreenCoordinates = this.getMousePosition(event);
        } else {
            let vectorDragging = false;

            this.nonBasisVectors.forEach(vector => {
                this.originalVectors = [];
                this.originalVectors.push(this.convertVectorToCanvasBasis(vector)); // canvas basis because we will need to transform them as mouse is dragged
                if (this.isMouseOnTip(this.getMouseVector(event), vector) && !vectorDragging) {
                    vector.isDragging = true;
                    vectorDragging = true;
                }
            });

            this.basisVectors.forEach(vector => {
                if (this.isMouseOnTip(this.getMouseVector(event), vector) && !vectorDragging) {
                    vector.isDragging = true;
                    vectorDragging = true;
                }
            });
        }
    }

    onMouseMove(event) {
        const mouseVector = this.getMouseVector(event);
        const currentScreenCoordinates = this.getMousePosition(event);

        if (this.isPanning) {
            this.translation.x = this.startTranslation.x + -1 * (currentScreenCoordinates.x - this.startScreenCoordinates.x) / D / this.scale;
            this.translation.y = this.startTranslation.y + (currentScreenCoordinates.y - this.startScreenCoordinates.y) / D / this.scale;
            this.draw();
        } else {
            this.nonBasisVectors.forEach(vector => {
                if (vector.isDragging) {
                    // Snap to nearest integer
                    const threshold = 0.08;
                    vector.x = (Math.abs(mouseVector.x - Math.round(mouseVector.x)) < threshold)
                        ? Math.round(mouseVector.x)
                        : mouseVector.x;
                    vector.y = (Math.abs(mouseVector.y - Math.round(mouseVector.y)) < threshold)
                        ? Math.round(mouseVector.y)
                        : mouseVector.y;
                }
                vector.isHovered = this.isMouseOnTip(mouseVector, vector);
                this.draw();
            });

            this.basisVectors.forEach(basisVector => {
                if (basisVector.isDragging) {
                    // Snap to nearest integer
                    const threshold = 0.08;
                    basisVector.x = (Math.abs(mouseVector.x - Math.round(mouseVector.x)) < threshold)
                        ? Math.round(mouseVector.x)
                        : mouseVector.x;
                    basisVector.y = (Math.abs(mouseVector.y - Math.round(mouseVector.y)) < threshold)
                        ? Math.round(mouseVector.y)
                        : mouseVector.y;

                    this.nonBasisVectors = []
                    this.originalVectors.forEach(vector => {
                        this.nonBasisVectors.push(this.getVectorCanvasMatrix().vectorProduct(vector));
                    });
                }
                basisVector.isHovered = this.isMouseOnTip(mouseVector, basisVector);
                this.draw();
            });
        }
    }

    convertVectorToCanvasBasis(vector) {
        return this.getVectorCanvasMatrix().inverse().vectorProduct(vector);
    }

    onMouseUp() {
        this.nonBasisVectors.forEach(vector => (vector.isDragging = false));
        this.basisVectors.forEach(vector => (vector.isDragging = false));
        this.isPanning = false;
        this.originalVectors = [];
    }

    onTouch(event) {
        event.preventDefault(); // Prevent default touch behavior

        if (event.touches.length === 2) {
            initialDistance = this.getTouchDistance(event.touches);
          }

        const touch = event.touches[0]; // Use the first touch point
        const touchPosition = { clientX: touch.clientX, clientY: touch.clientY };

        let vectorDragging = false;
        const mouseVector = this.getMouseVector(touchPosition);

        // Check if touch is on non-basis vectors
        this.nonBasisVectors.forEach(vector => {
            this.originalVectors = [];
            this.originalVectors.push(this.convertVectorToCanvasBasis(vector)); // Prepare for dragging transformations
            if (this.isMouseOnTip(mouseVector, vector) && !vectorDragging) {
                vector.isDragging = true;
                vectorDragging = true;
            }
        });

        // Check if touch is on basis vectors
        this.basisVectors.forEach(vector => {
            if (this.isMouseOnTip(mouseVector, vector) && !vectorDragging) {
                vector.isDragging = true;
                vectorDragging = true;
            }
        });

        // If no vector interaction, handle as a panning gesture
        if (!vectorDragging) {
            this.isPanning = true;
            this.startTranslation = {
                x: this.translation.x,
                y: this.translation.y,
            };
            this.startScreenCoordinates = this.getMousePosition(touchPosition);
        }
    }

    onTouchMove(event) {
        event.preventDefault(); // Prevent default touch actions like scrolling

        if (event.touches.length === 2) {
            event.preventDefault(); // Prevent page zoom
            const newDistance = this.getTouchDistance(event.touches);
            const scaleFactor = newDistance / initialDistance;
            currentScale *= scaleFactor;
            initialDistance = newDistance;
        
            console.log(`Zoom level: ${currentScale}`);
            // Perform your canvas scaling or zooming logic here
            return;
          }
    
        const touch = event.touches[0];
        const touchPosition = { clientX: touch.clientX, clientY: touch.clientY };
        const mouseVector = this.getMouseVector(touchPosition);
        const currentScreenCoordinates = this.getMousePosition(touchPosition);
    
        if (this.isPanning) {
            this.translation.x = this.startTranslation.x + -1 * (currentScreenCoordinates.x - this.startScreenCoordinates.x) / D / this.scale;
            this.translation.y = this.startTranslation.y + (currentScreenCoordinates.y - this.startScreenCoordinates.y) / D / this.scale;
            this.draw();
        } else {
            this.nonBasisVectors.forEach(vector => {
                if (vector.isDragging) {
                    // Snap to the nearest integer
                    const threshold = 0.08;
                    vector.x = Math.abs(mouseVector.x - Math.round(mouseVector.x)) < threshold
                        ? Math.round(mouseVector.x)
                        : mouseVector.x;
                    vector.y = Math.abs(mouseVector.y - Math.round(mouseVector.y)) < threshold
                        ? Math.round(mouseVector.y)
                        : mouseVector.y;
                }
                vector.isHovered = this.isMouseOnTip(mouseVector, vector);
                this.draw();
            });
    
            this.basisVectors.forEach(basisVector => {
                if (basisVector.isDragging) {
                    // Snap to the nearest integer
                    const threshold = 0.08;
                    basisVector.x = Math.abs(mouseVector.x - Math.round(mouseVector.x)) < threshold
                        ? Math.round(mouseVector.x)
                        : mouseVector.x;
                    basisVector.y = Math.abs(mouseVector.y - Math.round(mouseVector.y)) < threshold
                        ? Math.round(mouseVector.y)
                        : mouseVector.y;
    
                    this.nonBasisVectors = [];
                    this.originalVectors.forEach(vector => {
                        this.nonBasisVectors.push(this.getVectorCanvasMatrix().vectorProduct(vector));
                    });
                }
                basisVector.isHovered = this.isMouseOnTip(mouseVector, basisVector);
                this.draw();
            });
        }
    }

    onTouchEnd(event) {
        this.nonBasisVectors.forEach(vector => (vector.isDragging = false));
        this.basisVectors.forEach(vector => (vector.isDragging = false));
        this.isPanning = false;
        this.originalVectors = [];
    }    


    getTouchDistance(touches) {
        const [touch1, touch2] = touches;
        const dx = touch1.clientX - touch2.clientX;
        const dy = touch1.clientY - touch2.clientY;
        return Math.sqrt(dx * dx + dy * dy);
      }

    isMouseOnTip(mouseVector, vector) {
        return vector.distance(mouseVector) < 0.6 / this.scale;
    }

    onZoom(event) {
        const zoomFactor = 1.1;
        const mouseVector = this.getMouseVector(event);
        const scaleDirection = event.deltaY < 0 ? 1 : -1;
        const zoom = scaleDirection > 0 ? zoomFactor : 1 / zoomFactor;

        this.scale *= zoom;
        this.translation.x -= -1 * (mouseVector.x - this.translation.x) * (zoom - 1);
        this.translation.y -= -1 * (mouseVector.y - this.translation.y) * (zoom - 1);

        this.draw();
    }

    getMousePosition(event) {
        const rect = this.canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        return { x, y };
    }

    getMouseVector(event) {
        const rect = this.canvas.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;
        const vector = converter.screenToLogical({ x: mouseX, y: mouseY }, this.translation, this.scale, D);
        return new Vector([vector.x, -1 * vector.y], null);
    }

    getVectorCanvasMatrix() {
        return new Matrix(this.xBasis, this.yBasis);
    }

    draw() {
        //this.ctx.setTransform(this.scale, 0, 0, this.scale, this.translation.x, this.translation.y);
        this.ctx.fillStyle = 'black';
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        this.drawStandardBasis();
        this.drawLines();
        this.nonBasisVectors.forEach(vector => this.drawVector(vector));
        this.basisVectors.forEach(vector => this.drawVector(vector, defaultLineWidth + 1));

        const eigenvalues = new Matrix(this.xBasis, this.yBasis).eigenvalues();
        this.eigenvectors[0].x = this.yBasis.x;
        this.eigenvectors[0].y = this.xBasis.x - eigenvalues.x1;
        this.eigenvectors[1].x = this.yBasis.x;
        this.eigenvectors[1].y = this.xBasis.x - eigenvalues.x2;
        //this.eigenvectors.forEach(vector => vector.normalize());
        this.eigenvectors.forEach(vector => this.drawVector(vector));

        this.drawOrigin();
        return { basisVectors: this.basisVectors, eigenvectors: this.eigenvectors, eigenvalues: eigenvalues, nonBasisVectors: this.nonBasisVectors };
    }

    drawOrigin() {
        this.ctx.fillStyle = 'white';

        this.ctx.beginPath();
        const screenCoordinates = converter.logicalToScreen(new Vector([0, 0], null), this.translation, this.scale, D);
        this.ctx.arc(screenCoordinates.x, screenCoordinates.y, 5 * this.scale, 0, Math.PI * 2);
        this.ctx.fill();
    }

    drawLines() {
        this.ctx.strokeStyle = "#AFE6FB";
        this.ctx.lineWidth = 1 * this.scale;
        for (let i = -20; i < 20; i++) {
            this.drawLine(this.xBasis.scale(i), this.yBasis);
            this.drawLine(this.yBasis.scale(i), this.xBasis);
        }
    }

    drawStandardBasis() {
        this.ctx.strokeStyle = "#85969C";
        this.ctx.lineWidth = 0.5 * this.scale;
        for (let i = -20; i < 20; i++) {
            this.ctx.lineWidth = i === 0 ? 3 : defaultLineWidth;
            this.ctx.strokeStyle = i === 0 ? "WHITE" : "GREY";
            this.drawLine(this.iHat.scale(i), this.jHat);
            this.drawLine(this.jHat.scale(i), this.iHat);
        }
    }

    drawLine(offsetVector, basisVector) {
        const screenCoordinates = converter.logicalToScreen(offsetVector, this.translation, this.scale, D);
        const lineLength = 1000;
        this.ctx.beginPath();
        this.ctx.moveTo(screenCoordinates.x + lineLength * this.scale * basisVector.x, screenCoordinates.y - lineLength * this.scale * basisVector.y);
        this.ctx.lineTo(screenCoordinates.x - lineLength * this.scale * basisVector.x, screenCoordinates.y + lineLength * this.scale * basisVector.y);

        this.ctx.stroke();
    }

    drawVector(vector, width = defaultLineWidth, dotted = false) {
        const lineWidth = ((vector.isHovered &&
            this.nonBasisVectors.forEach(vector => (vector.isHovered === false)) &&
            this.basisVectors.forEach(vector => (vector.isHovered === false))) || vector.isDragging ? 2 * width : width) * this.scale;
        const lineShorten = 15 * this.scale;
        const angle = Math.atan2(vector.y, vector.x);
        const screenCoordinates = converter.logicalToScreen(vector, this.translation, this.scale, D);
        const screenOrigin = converter.logicalToScreen(this.ORIGIN, this.translation, this.scale, D);

        this.ctx.beginPath();
        this.ctx.moveTo(screenOrigin.x, screenOrigin.y);
        this.ctx.lineTo(screenCoordinates.x - lineShorten * Math.cos(angle), screenCoordinates.y + lineShorten * Math.sin(angle));
        this.ctx.strokeStyle = vector.color;
        this.ctx.lineWidth = lineWidth;
        this.ctx.stroke();

        this.ctx.font = "bold 40px Latin Modern Math";
        this.ctx.fillStyle = vector.color || "white";
        this.ctx.fillText(vector.name, screenCoordinates.x - 10 + 15 * Math.cos(angle), screenCoordinates.y + 10 - 15 * Math.sin(angle));

        this.drawArrowhead(vector);
    }

    drawArrowhead(vector) {
        const headLength = 20 * this.scale;
        const angle = Math.atan2(-vector.y, vector.x);
        const headVector = converter.logicalToScreen(vector, this.translation, this.scale, D);
        const arrowWidth = vector.isHovered || vector.isDragging ? 10 : 5;

        this.ctx.beginPath();
        this.ctx.moveTo(headVector.x, headVector.y);
        this.ctx.lineTo(
            headVector.x - headLength * Math.cos(angle - Math.PI / 6),
            headVector.y - headLength * Math.sin(angle - Math.PI / 6)
        );
        this.ctx.lineTo(
            headVector.x - headLength * Math.cos(angle + Math.PI / 6),
            headVector.y - headLength * Math.sin(angle + Math.PI / 6)
        );
        this.ctx.lineTo(headVector.x, headVector.y);
        this.ctx.fillStyle = vector.color;
        this.ctx.lineWidth = arrowWidth;
        this.ctx.fill();
    }
}

export default VectorCanvas